import type { CubeFile, FileStatus } from '@stores/conversionStore';
import styles from './FileList.module.css';

export interface FileListProps {
  files: CubeFile[];
  onRemove: (id: string) => void;
  disabled?: boolean;
}

const STATUS_CONFIG: Record<FileStatus, { label: string; className: string }> = {
  pending:    { label: 'Pending',    className: 'statusPending' },
  validating: { label: 'Validating', className: 'statusConverting' },
  valid:      { label: 'Valid',      className: 'statusValid' },
  invalid:    { label: 'Invalid',    className: 'statusFailed' },
  converting: { label: 'Converting', className: 'statusConverting' },
  complete:   { label: 'Complete',   className: 'statusValid' },
  failed:     { label: 'Failed',     className: 'statusFailed' },
};

export function FileList({ files, onRemove, disabled = false }: FileListProps) {
  if (files.length === 0) {
    return (
      <div className={styles.empty}>
        No files added yet. Drop files above or click to select.
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>File</th>
            <th>Size</th>
            <th>Status</th>
            <th style={{ width: '50px' }} />
          </tr>
        </thead>
        <tbody>
          {files.map((file) => {
            const config = STATUS_CONFIG[file.status];
            return (
              <tr key={file.id}>
                <td>
                  <div className={styles.filename}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16" style={{ flexShrink: 0, color: 'var(--text-m)' }}>
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14,2 14,8 20,8" />
                    </svg>
                    <div>
                      <span className={styles.filenameText}>{file.filename}</span>
                      {file.error && <span className={styles.fileError}>{file.error}</span>}
                    </div>
                  </div>
                </td>
                <td className={styles.size}>{formatFileSize(file.size)}</td>
                <td>
                  <span className={`${styles.badge} ${styles[config.className]}`}>
                    {config.label}
                  </span>
                </td>
                <td>
                  <button
                    className={styles.removeBtn}
                    onClick={() => onRemove(file.id)}
                    disabled={disabled || file.status === 'converting'}
                    title="Remove file"
                    aria-label={`Remove ${file.filename}`}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default FileList;
